import React, { useState } from 'react';
import { Container } from '../ui/Container';
import { Button } from '../ui/Button';
import { PACKAGES, COUPONS, PAGE_EDITION_MAPPER, FEATURES, INTEGRATIONS, getOfferPrice, getPageActiveOffer, getEditionPricing } from '../../Constants/Packages';
import ConditionalDiv from '../Common/ConditionalDiv';
import { useIsMobile } from '../../Hooks/useIsMobile';
import { FLASHOFFER } from '../../Constants/Const';
import { useBrand } from '../../Utils/BrandContext';
import apiService from '../../Utils/APIService';
import { isIOSMobileDevice } from '../../Utils/Utilities';
import IOSRestrictedModal from '../Common/IOSRestrictedModal';

const PackagesSection = ({ onBuyNow, showCoupons = false, pageName = 'GERP', isFirstSection = false, isCpnExpanded = false, onCouponView, onPackageClick, onPackageView, onGetOffer, isFromOfr = false, forceOfferKey = null, isFromWebsite = false }) => {
  let visibleEditions = [];
  const [showIOSModal, setShowIOSModal] = useState(false);
  const isMobile = useIsMobile()
  const activeOffer = getPageActiveOffer(pageName, forceOfferKey)
  const flashOfferOn = activeOffer?.offerKey === 'FLASH_EOD'
  const sundayOfferOn = activeOffer?.offerKey === 'SUNDAY'
  const d3OfferOn = activeOffer?.offerKey === 'D3'
  // Any offer that isn't Flash/Sunday/Fresh — D3, OLD_PL, FDBK_CLRNC, etc.
  const hasGenericOffer = !!activeOffer && !flashOfferOn && !sundayOfferOn && activeOffer?.offerKey !== 'FRESH'
  const { brand: activeBrand } = useBrand()


  if (pageName && PAGE_EDITION_MAPPER[pageName.toLowerCase()]) {
    const pageConfig = PAGE_EDITION_MAPPER[pageName.toLowerCase()];
    visibleEditions = pageConfig.editionIds
      .map(id => PACKAGES[id])
      .filter(edition => edition)
      // .filter(edition => activeBrand === 'qikerp' ? edition.id !== 34 && edition.id !== 35 : edition.id !== 36);
      .filter(edition => edition); // just keep all editions from the mapper
  }

  // Modal state
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [selectedEditionForCoupons, setSelectedEditionForCoupons] = useState(null);
  const { showMM, showYY } = { showMM: false, showYY: true };

  const features = FEATURES;
  const integrations = INTEGRATIONS;

  const calculateDiscount = (stdAmt, afterDsc) => {
    if (!stdAmt || stdAmt === 0) return 0;
    return Math.round(((stdAmt - afterDsc) / stdAmt) * 100);
  };

  const getDisplayPrice = (edition) => {
    // GERP page override: baseAmt as strikethrough, baseAmtAfterDesc as main price
    if (pageName && pageName.toUpperCase() === 'GERP' && edition.baseAmtAfterDesc && edition.baseAmtAfterDesc < edition.baseAmt) {
      return {
        mainPrice: edition.baseAmtAfterDescStr || String(edition.baseAmtAfterDesc),
        mainPriceAmount: edition.baseAmtAfterDesc,
        strikethroughPrice: edition.baseAmtStr || null,
        strikethroughAmount: edition.baseAmt || null,
        cutPrice: null, cutAmount: null,
        discountPercent: calculateDiscount(edition.baseAmt, edition.baseAmtAfterDesc),
        showStrikethrough: true, showCutthrough: false, offerLabel: ''
      };
    }
    // Priority order for price display:
    // 1. If flashOfferOn, show afterDsc as main price
    // 2. If showD1Offer, show afterDsc as main price
    // 3. If showStdOffer, show baseAmt as main price
    // 4. Default: show baseAmt as main price

    const hasSpecialOffer = edition.flashOfferOn || edition.showD1Offer || edition.showStdOffer;

    if (edition.flashOfferOn || edition.showD1Offer) {
      return {
        mainPrice: edition.afterDscStr || edition.afterDsc,
        mainPriceAmount: edition.afterDsc,
        strikethroughPrice: edition.stdAmtStr || null,
        strikethroughAmount: edition.stdAmt || null,
        cutPrice: edition.baseAmtStr,
        cutAmount: edition.baseAmt,
        discountPercent: edition.discPercent,
        showStrikethrough: true,
        showCutthrough: true,
        offerLabel: edition.flashSaleName || 'Special Offer'
      };
    } else if (edition.stdAmt && edition.stdAmt > edition.baseAmt) {
      // Show standard price strikethrough with base amount as main
      return {
        mainPrice: edition.baseAmtStr,
        mainPriceAmount: edition.baseAmt,
        strikethroughPrice: edition.stdAmtStr,
        strikethroughAmount: edition.stdAmt,
        cutPrice: null,
        cutAmount: null,
        discountPercent: calculateDiscount(edition.stdAmt, edition.baseAmt),
        showStrikethrough: true,
        showCutthrough: false
      };
    } else {
      // Default: show base amount as main price
      return {
        mainPrice: edition.baseAmtStr,
        mainPriceAmount: edition.baseAmt,
        strikethroughPrice: null,
        strikethroughAmount: null,
        cutPrice: null,
        cutAmount: null,
        discountPercent: 0,
        showStrikethrough: false,
        showCutthrough: false
      };
    }
  };

  const getMonthlyDisplayPrice = (monthlyData) => {
    if (!monthlyData) return null;

    return {
      mainPrice: monthlyData.baseAmtStr || monthlyData.baseAmt,
      mainPriceAmount: monthlyData.baseAmt,
      discountPercent: monthlyData.discPercent || 0
    };
  };

  const renderQuotasDisplay = (quotas) => {
    if (!quotas || quotas.length === 0) {
      return null;
    }
    return quotas.map((quota, idx) => (
      <React.Fragment key={idx}>
        <span dangerouslySetInnerHTML={{ __html: quota }} />
        {idx < quotas.length - 1 && ', '}
      </React.Fragment>
    ));
  };

  const getDefaultCouponForEdition = (editionID) => {
    return Object.values(COUPONS).find(coupon => coupon.editionID === editionID && coupon.default === true) || null;
  };

  const handleBuyNowWithDefaultCoupon = (editionId) => {
    const allDataRaw = localStorage.getItem('allData');
    if (!allDataRaw) {
      onBuyNow(editionId); // fallback — scroll to hero
      return;
    }

    const defaultCoupon = getDefaultCouponForEdition(editionId);
    if (defaultCoupon) {
      handleUseCoupon(defaultCoupon.code, editionId, defaultCoupon.planYear === 2 ? 'YY2' : 'YY');
    } else {
      onBuyNow(editionId);
    }
  };

  const getCouponsByEdition = (editionID) => {
    return Object.values(COUPONS).filter(coupon => {
      if (!coupon.isActive) return false;
      if (coupon.editionID !== editionID) return false;

      // Source based coupon filtering
      const allDataRaw = localStorage.getItem('allData');
      if (allDataRaw && coupon.cpnFor && coupon.cpnFor !== 'ALL') {
        try {
          const allData = JSON.parse(allDataRaw);
          const source = (allData?.source || '');
          const isWHFB = source.startsWith('FB_') || source.endsWith('_WH');
          if (coupon.cpnFor === 'META' && !isWHFB) return false;
          if (coupon.cpnFor === 'NONMETA' && isWHFB) return false;
        } catch {
          return false;
        }
      }

      const allData = JSON.parse(allDataRaw);
      const sinceDTStr = allData?.sinceDTStr || null;
      const now = new Date();
      const sinceDateStr = sinceDTStr ? sinceDTStr.split(' ')[0] : '';
      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const isFirstDay = sinceDateStr === todayStr;
      if (coupon.hideOnDay1 && isFirstDay) {
        console.log(coupon.hideOnDay1, isFirstDay)
        return false;
      }

      // Case 3: age >= case3MinDay — always show (must run BEFORE minDay/maxDay check)
      if (coupon.case3MinDay !== undefined) {
        const now3 = new Date();
        const todayStr3 = `${now3.getFullYear()}-${String(now3.getMonth() + 1).padStart(2, '0')}-${String(now3.getDate()).padStart(2, '0')}`;
        const sinceDate3 = new Date(sinceDateStr);
        const today3 = new Date(todayStr3);
        const ageDays3 = Math.floor((today3 - sinceDate3) / (1000 * 60 * 60 * 24));
        if (ageDays3 >= coupon.case3MinDay) return true;
      }

      if (coupon.maxDay !== undefined || coupon.minDay !== undefined) {
        const now2 = new Date();
        const todayStr2 = `${now2.getFullYear()}-${String(now2.getMonth() + 1).padStart(2, '0')}-${String(now2.getDate()).padStart(2, '0')}`;
        const sinceDate2 = new Date(sinceDateStr);
        const today2 = new Date(todayStr2);
        const ageDays2 = Math.floor((today2 - sinceDate2) / (1000 * 60 * 60 * 24));
        if (coupon.minDay !== undefined && ageDays2 < coupon.minDay) return false;
        if (coupon.maxDay !== undefined && ageDays2 > coupon.maxDay) return false;
      }

      // Case 1: Day 1, hour 0–9
      const isCase1 = (() => {
        if (coupon.minDay === undefined && coupon.maxDay === undefined && coupon.startHour === undefined) return false;
        const now2 = new Date();
        const todayStr2 = `${now2.getFullYear()}-${String(now2.getMonth() + 1).padStart(2, '0')}-${String(now2.getDate()).padStart(2, '0')}`;
        const sinceDate2 = new Date(sinceDateStr);
        const today2 = new Date(todayStr2);
        const ageDays2 = Math.floor((today2 - sinceDate2) / (1000 * 60 * 60 * 24));
        const currentHour = now2.getHours();
        return ageDays2 === 1 && currentHour >= 0 && currentHour < 9;
      })();

      // Case 2: Lead age >= 1, first visit (OfrStartDT just set), within 15-min rounded window
      const isCase2 = (() => {
        if (!coupon.case2Window) return false;
        try {
          const allDataRaw2 = localStorage.getItem('allData');
          if (!allDataRaw2) return false;
          const allData2 = JSON.parse(allDataRaw2);
          const leadInfo2 = allData2.info ? JSON.parse(allData2.info) : [];
          const ofrStartDT = leadInfo2[0]?.OfrStartDT || allData2?.OfrStartDT || null;
          if (!ofrStartDT) return false;
          const startTime = new Date(ofrStartDT.replace(' ', 'T'));
          const rawExpiry = new Date(startTime.getTime() + 15 * 60 * 1000);
          const roundedMin = Math.ceil(rawExpiry.getMinutes() / 15) * 15;
          const expiresAt = new Date(rawExpiry);
          expiresAt.setMinutes(roundedMin, 0, 0);
          return new Date() <= expiresAt;
        } catch { return false; }
      })();

      if (coupon.startHour !== undefined && coupon.endHour !== undefined || coupon.case2Window) {
        // If Case 1 is active, show via Case 1 only — skip Case 2 to avoid showing twice
        if (isCase1) return true;
        // If Case 2 is active and Case 1 is not, show via Case 2
        if (isCase2) return true;
        // Neither case active — hide
        return false;
      }

      // Timed coupon check: only show if within ofrStartDT + 15 min (rounded) window
      if (coupon.timedWindow === true && !coupon.extendedTo8PM) {
        try {
          const allDataRaw = localStorage.getItem('allData');
          if (!allDataRaw) return false;
          const allData = JSON.parse(allDataRaw);
          const leadInfo = allData.info ? JSON.parse(allData.info) : [];
          const ofrStartDT = leadInfo[0]?.OfrStartDT || allData?.OfrStartDT || null;
          if (!ofrStartDT) return false;
          const startTime = new Date(ofrStartDT.replace(' ', 'T'));
          const rawExpiry = new Date(startTime.getTime() + 15 * 60 * 1000);
          const roundedMin = Math.ceil(rawExpiry.getMinutes() / 15) * 15;
          const expiresAt = new Date(rawExpiry);
          expiresAt.setMinutes(roundedMin, 0, 0);
          return new Date() <= expiresAt;
        } catch {
          return false;
        }
      }

      // extendedTo8PM timed coupons: only show if:
      //   1. Lead was received TODAY (sinceDTStr date == today)
      //   2. Current time is BEFORE 8 PM (hour < 20)
      //      → At 8 PM+, FLASH_EOD offer takes over; hide this coupon to avoid overlap
      if (coupon.timedWindow === true && coupon.extendedTo8PM === true) {
        try {
          const now = new Date();
          const currentHour = now.getHours();
          // After 8 PM, FLASH_EOD is active — suppress extendedTo8PM coupons
          if (currentHour >= 20) return false;
          const allDataRaw = localStorage.getItem('allData');
          if (!allDataRaw) return false;
          const allData = JSON.parse(allDataRaw);
          const sinceDTStr = allData?.sinceDTStr || null;
          if (!sinceDTStr) return false;
          // Extract date part: "2026-03-11 14:26:00" → "2026-03-11"
          const sinceDateStr = sinceDTStr.split(' ')[0];
          const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
          return sinceDateStr === todayStr;
        } catch {
          return false;
        }
      }

      return true;
    });
  };

  const handleViewCoupons = (edition) => {
    setSelectedEditionForCoupons(edition);
    setShowCouponModal(true);
  };

  const handleUseCoupon = (couponCode, editionId, forcePeriod = null) => {
    if (isIOSMobileDevice()) {
      setShowIOSModal(true);
      return;
    }
    onGetOffer?.()
    // 1. Get lead info from context/storage
    const allDataRaw = localStorage.getItem('allData');
    if (!allDataRaw) return;

    const allData = JSON.parse(allDataRaw);
    const leadInfo = allData.info ? JSON.parse(allData.info) : [];
    const leadO = leadInfo[0]?.LeadO || '';
    const mobile = allData.mobile || '';

    // 2. Determine plan period - forcePeriod overrides coupon lookup (used for flash offer)
    let selectedPeriod = forcePeriod || "YY";
    let isYear2 = forcePeriod === "YY2";

    if (!forcePeriod) {
      const couponDetails = Object.values(COUPONS).find(
        (c) => c.code === couponCode && c.editionID === editionId
      );
      if (couponDetails) {
        selectedPeriod = couponDetails.planYear === 1 ? "YY" : `YY${couponDetails.planYear}`;
        isYear2 = couponDetails.planYear === 2;
      }
    }

    // 3. Set essential payment items
    localStorage.setItem("selEdn", editionId);
    localStorage.setItem("selPrd", selectedPeriod);
    localStorage.setItem(isYear2 ? "cpnYY2" : "cpn", couponCode);
    localStorage.setItem("guidePG", window.location.pathname.substring(1));

    // 4. Ensure cio is present (BZPay requirement)
    const cioData = [{ 'i': mobile, 'o': leadO }];
    localStorage.setItem('cio', JSON.stringify(cioData));

    // 5. Redirect
    const currentParams = window.location.search;
    window.location.href = "/Html/External/BZPay.html" + currentParams;
  };
  const CouponModal = () => {
    if (!showCouponModal || !selectedEditionForCoupons) return null;

    const coupons = getCouponsByEdition(selectedEditionForCoupons.id);
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 ">
        <div className="bg-[#f1f1f0] rounded max-w-sm w-full overflow-y-auto">
          {/* Modal Header */}
          <div className="sticky top-0 p-4 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-text-primary">Discount Coupons</h2>
            <button
              onClick={() => setShowCouponModal(false)}
              className="text-2xl text-text-secondary hover:text-text-primary"
            >
              ✕
            </button>
          </div>

          {/* Modal Content */}
          <div className="px-4">
            <div className="space-y-2">
              {/* Flash Offer Box - shown only if flashOfferOn */}
              {flashOfferOn && (
                <div className="rounded border border-primary bg-primary-subtle p-4">
                  <h3 className="text-lg font-bold text-primary mb-2">Offer Ending Soon!</h3>
                  <p className="text-sm text-text-secondary mb-1">
                    Get additional Discount
                  </p>
                  <p className="text-sm text-text-primary mb-3">
                    <span
                      className="font-mono font-bold text-primary"
                      style={{ animation: 'blink 1.2s step-start infinite' }}
                    >
                      FLASHOFFER
                    </span>
                  </p>
                  <style>{`@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>
                  <Button
                    onClick={() => handleUseCoupon('FLASHOFFER', selectedEditionForCoupons.id, 'YY2')}
                    size="sm"

                  >
                    <i className="fa fa-tag mr-2"></i> Get Offer
                  </Button>
                </div>
              )}

              {sundayOfferOn && (
                <div className="rounded border border-primary bg-primary-subtle p-4">
                  <h3 className="text-lg font-bold text-primary-600 mb-2">Sunday Special!</h3>
                  <p className="text-sm text-text-secondary mb-1">
                    Extra discount off on your first year
                  </p>
                  <p className="text-sm text-text-primary mb-3">
                    <span className="font-mono font-bold text-primary">
                      SUNSPL30
                    </span>
                  </p>
                  <Button
                    onClick={() => handleUseCoupon('SUNSPL30', selectedEditionForCoupons.id, 'YY')}
                    size="sm"
                  >
                    <i className="fa fa-tag mr-2"></i> Get Offer
                  </Button>
                </div>
              )}

              {/* Regular Coupons */}
              {coupons.length > 0 ? (
                coupons.map((coupon) => (
                  <div key={coupon.id} className="border-2 bg-white border-border rounded p-4">
                    <h3 className="text-lg font-bold text-text-primary mb-2">
                      {coupon.title.includes('₹')
                        ? coupon.title.split(/(₹)/g).map((part, i) =>
                          part === '₹' ? <span key={i} className="text-sm">₹</span> : part
                        )
                        : coupon.title}
                    </h3>
                    <p className="text-sm text-text-secondary mb-3">
                      {coupon.description.includes('₹')
                        ? coupon.description.split(/(₹)/g).map((part, i) =>
                          part === '₹' ? <span key={i} className="text-xs">₹</span> : part
                        )
                        : coupon.description}
                      {coupon.showCpnCode == true && (
                        <> using coupon code: {' '}
                          <span className="font-mono font-bold text-primary">{coupon.code}</span>
                        </>
                      )}
                    </p>
                    <Button
                      onClick={() => handleUseCoupon(coupon.code, selectedEditionForCoupons.id)}
                      size="sm"
                    >
                      <i className="fa fa-tag mr-2"></i> Get Offer
                    </Button>
                    {coupon.showOfrEndLabel === true && coupon.timedWindow === true && (() => {
                      try {
                        const allDataRaw = localStorage.getItem('allData');
                        if (!allDataRaw) return null;
                        const allData = JSON.parse(allDataRaw);
                        const leadInfo = allData.info ? JSON.parse(allData.info) : [];
                        const ofrStartDT = leadInfo[0]?.OfrStartDT || allData?.OfrStartDT || null;
                        if (!ofrStartDT) return null;
                        const startTime = new Date(ofrStartDT.replace(' ', 'T'));
                        const rawExpiry = new Date(startTime.getTime() + 15 * 60 * 1000);
                        const roundedMin = Math.ceil(rawExpiry.getMinutes() / 15) * 15;
                        const rounded = new Date(rawExpiry);
                        rounded.setMinutes(roundedMin, 0, 0);
                        const now = new Date();
                        const todayMidnight = new Date(now); todayMidnight.setHours(23, 59, 59, 999);
                        const dayLabel = rounded <= todayMidnight ? 'today' : 'tomorrow';
                        const hh = rounded.getHours();
                        const mm = rounded.getMinutes();
                        const ampm = hh >= 12 ? 'PM' : 'AM';
                        const displayHour = hh % 12 === 0 ? 12 : hh % 12;
                        const displayMin = String(mm).padStart(2, '0');
                        const timeStr = `${displayHour}:${displayMin} ${ampm}`;
                        // isExpired = 15-min rounded window has passed
                        const isExpired = now > rounded;
                        return (
                          <p className="text-sm font-bold text-primary mt-3" style={{ animation: 'blink 1.2s step-start infinite' }}>
                            {isExpired && coupon.extendedTo8PM ? 'Offer Ending Soon!' : `Valid ${dayLabel} until ${timeStr}`}
                          </p>
                        );
                      } catch { return null; }
                    })()}
                    {coupon.showOfrEndLabel === true && !coupon.timedWindow && (
                      <p className="text-sm font-bold text-primary mt-3">
                        {coupon.morningLabel && coupon.startHour !== undefined && coupon.endHour !== undefined
                          && new Date().getHours() >= coupon.startHour && new Date().getHours() < coupon.endHour
                          ? coupon.morningLabel
                          : 'Limited Time Offer!'}
                      </p>
                    )}
                  </div>
                ))
              ) : (
                !flashOfferOn && (
                  <p className="text-center text-text-secondary py-8">
                    No coupons available for this edition at the moment.
                  </p>
                )
              )}
            </div>
          </div>

          {/* Modal Footer */}
          <div className="p-4 flex justify-start">
            <Button variant="secondary" size="sm" className="bg-background-light" onClick={() => setShowCouponModal(false)}> Close </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderPackageCardMobile = (edition) => {
    const availableCoupons = getCouponsByEdition(edition.id);
    const priceDisplay = getDisplayPrice(edition);
    const offerPrice = getOfferPrice(edition, activeOffer);

    return (
      <div
        key={edition.id}
        className="w-full bg-background-light border border-border rounded p-3 text-left flex flex-col shadow-[0_2px_5px_rgba(0,0,0,0.05)]"
      >
        <h2 className="text-[24px] font-bold mt-0 mb-0 text-text-primary">
          {edition.name}
        </h2>
        <p className="text-[14px] text-text-primary mb-3 italic">
          {edition.tagline || 'Complete business solution for your enterprise'}
        </p>

        {edition.quotas && edition.quotas.length > 0 && (
          <p className="text-sm text-text-primary mb-3">
            Quotas: {renderQuotasDisplay(edition.quotas)}
          </p>
        )}

        {/* Flash Sale or Special Offer Label */}
        {(edition.flashOfferOn || edition.showD1Offer) && (
          <div className="mb-2 bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded text-[12px] font-semibold">
            {priceDisplay.offerLabel}
          </div>
        )}
        {/* MOBILE PRICE BOX — show only one panel: Flash > Sunday > D3 > Standard */}
        {flashOfferOn ? (
          <div className="mb-3 border-2 border-primary bg-primary-subtle rounded p-3">
            <h2 className="m-0 font-bold text-primary text-[19px]">Flash Offer</h2>
            <p className="text-[15px] font-semibold mb-2 text-text-primary mt-2">Get second year free</p>
            <div className="flex items-baseline gap-1 flex-wrap mb-3">
              <span className="text-[11px] text-text-secondary">₹</span>
              <span className="text-sm font-bold text-text-secondary line-through">{offerPrice.stdPrice}</span>
              <span className="text-[14px] text-text-secondary ml-1">₹</span>
              <span className="text-2xl font-extrabold text-text-primary">{offerPrice.displayPrice}</span>
              <span className="text-xs text-text-secondary">/ year +GST</span>
            </div>
            <Button
              onClick={() => { handleUseCoupon('FLASHOFFER', edition.id, 'YY'); onPackageClick?.(edition.id); }}
              variant="primary"
              className="w-full"
            >
              Get Discount Offer
            </Button>
          </div>
        ) : sundayOfferOn ? (
          <div className="mb-3 border-2 border-primary bg-primary-subtle rounded p-3">
            <h2 className="m-0 font-bold text-primary text-[19px]">Sunday Special</h2>
            <p className="text-[15px] font-semibold mb-2 text-text-primary mt-2">Extra {Math.round((1 - Number(offerPrice.displayPrice.replace(/,/g, '')) / Number(offerPrice.stdPrice.replace(/,/g, ''))) * 100)}% off on first year</p>
            <div className="flex items-baseline gap-1 flex-wrap mb-3">
              <span className="text-[11px] text-text-secondary">₹</span>
              <span className="text-sm font-bold text-text-secondary line-through">{offerPrice.stdPrice}</span>
              <span className="text-[14px] text-text-secondary ml-1">₹</span>
              <span className="text-2xl font-extrabold text-text-primary">{offerPrice.displayPrice}</span>
              <span className="text-xs text-text-secondary">+GST</span>
            </div>
            <Button
              onClick={() => { handleUseCoupon('SUNSPL30', edition.id, 'YY'); onPackageClick?.(edition.id); }}
              variant="primary"
              className="w-full"
            >
              Get Discount Offer
            </Button>
          </div>
        ) : hasGenericOffer ? (
          <div className="mb-3 border-2 border-primary bg-primary-subtle rounded p-3">
            <h2 className="m-0 font-bold text-primary text-[19px]">Limited Time Offer</h2>
            <p className="text-[15px] font-semibold mb-2 text-text-primary mt-2">Extra {Math.round((1 - Number(offerPrice.displayPrice.replace(/,/g, '')) / Number(offerPrice.stdPrice.replace(/,/g, ''))) * 100)}% off for First Year — Today Only!</p>
            <div className="flex items-baseline gap-1 flex-wrap mb-3">
              <span className="text-[11px] text-text-secondary">₹</span>
              <span className="text-sm font-bold text-text-secondary line-through">{offerPrice.stdPrice}</span>
              <span className="text-[14px] text-text-secondary ml-1">₹</span>
              <span className="text-2xl font-extrabold text-text-primary">{offerPrice.displayPrice}</span>
              <span className="text-xs text-text-secondary">+GST</span>
            </div>
            <Button
              onClick={() => { handleUseCoupon(activeOffer.couponCode, edition.id, activeOffer.planPeriod); onPackageClick?.(edition.id); }}
              variant="primary"
              className="w-full"
            >
              Get Discount Offer
            </Button>
          </div>
        ) : (
          <div className="bg-white border-2 border-border rounded p-3 mb-0">
            {priceDisplay.showStrikethrough && priceDisplay.strikethroughPrice && (
              <div className="mb-2">
                <span className="text-sm text-text-primary">Regular Rate: </span>
                <span className="font-bold text-text-secondary">
                  <span className="text-[11px]">₹ </span>{priceDisplay.strikethroughPrice}
                  <span className="text-[11px]"> / year</span>
                </span>
              </div>
            )}
            {priceDisplay.showCutthrough && priceDisplay.cutPrice && (
              <div className="mb-2">
                <span className="text-sm text-text-primary">Regular Price: </span>
                <span className="font-bold text-text-secondary">
                  <span className="text-[11px]">₹ </span>{priceDisplay.cutPrice}
                  <span className="text-[11px]"> / year</span>
                </span>
              </div>
            )}
            <div className="mb-3">
              <div className="flex items-baseline gap-1 flex-wrap">
                <span className="text-sm text-text-primary">Offer Rate:</span>
                <span className="text-sm text-text-secondary">₹</span>
                <span className="text-2xl font-extrabold text-text-primary">{priceDisplay.mainPrice}</span>
                <span className="text-xs text-text-secondary">/ year</span>
              </div>
              {priceDisplay.discountPercent > 0 && (
                <>
                  <style>{`@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>
                  <span className="text-lg font-bold text-primary" style={{ animation: 'blink 1.2s step-start infinite' }}>{priceDisplay.discountPercent}% OFF!</span>

                </>
              )}
            </div>
            {/* <p className="text-xs text-text-secondary mb-4">(GST extra. Limited time offer.)</p> */}
            <Button
              onClick={() => { handleBuyNowWithDefaultCoupon(edition.id); onPackageClick?.(edition.id); }}
              variant="primaryDarkBlue"
              className="w-full"
            >
              Buy Now
            </Button>
            {showCoupons && availableCoupons.length > 0 && (
              <Button
                onClick={() => { handleViewCoupons(edition); onCouponView?.(edition.name); }}
                variant="secondaryDarkBlue"
                className="w-full mt-3"
              >
                View Discount Offers
              </Button>
            )}
          </div>
        )}

        {/* View Discount Coupons
        {showCoupons && availableCoupons.length > 0 && (
          <>
            {!isCpnExpanded ? (
              <button
                onClick={() => { handleViewCoupons(edition); onCouponView?.(edition.name); }}
                className="w-full mt-3 bg-primary text-white font-semibold py-2 px-4 rounded border transition-colors flex items-center justify-between"
              >
                <span>View Discount Coupons</span>
                <span>›</span>
              </button>
            ) : (
              <div className="mt-3 space-y-2">
                <h3 className="text-base font-bold text-text-primary mb-2">Available Discount Coupons:</h3>
                {availableCoupons.map((coupon) => (
                  <div key={coupon.id} className="border-2 bg-white border-border rounded p-3">
                    <h4 className="text-sm font-bold text-text-primary mb-1">{coupon.title}</h4>
                    <p className="text-xs text-text-secondary mb-3">
                      {coupon.description} using coupon code:{' '}
                      <span className="font-mono font-bold text-primary">{coupon.code}</span>
                    </p>
                    <Button onClick={() => handleUseCoupon(coupon.code, edition.id)} size="sm">
                      <i className="fa fa-tag mr-2"></i> Get Offer
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </>
        )} */}

      </div>
    );
  };

  const renderPackageCard = (edition = 36) => {
    const discountPercent = calculateDiscount(edition.stdAmt, edition.baseAmt);
    const availableCoupons = getCouponsByEdition(edition.id);
    const priceDisplay = getDisplayPrice(edition);
    const offerPrice = getOfferPrice(edition, activeOffer); // stdPrice=strikethrough, displayPrice=offer price
    const monthlyDisplay = edition.monthly ? getMonthlyDisplayPrice(edition.monthly) : null;
    const showBothPrices = showYY && showMM && edition.monthly;

    return (
      <div
        key={edition.id}
        className={`flex-1 min-w-[300px] max-w-[450px] ${isFromOfr ? 'bg-background-light' : 'bg-white'} border border-border rounded p-3 text-left flex flex-col shadow-[0_2px_5px_rgba(0,0,0,0.05)]`}
      >
        <h2 className={`text-[27px] font-bold [text-shadow:1px_1px_1px_rgba(0,0,0,0.1)] mt-0 mb-0 text-text-primary
          }`}>
          {edition.name}
        </h2>
        <p className="text-[15px] text-text-primary mb-4 italic">
          {edition.tagline || 'Complete business solution for your enterprise'}
        </p>

        <div className="text-sm leading-relaxed flex-grow mb-5 text-text-primary">
          <p>All features included</p>
          {edition.quotas && edition.quotas.length > 0 && (
            <p className="mt-4">
              Quotas: {renderQuotasDisplay(edition.quotas)}
            </p>
          )}
          <p className="mt-4">
            Also covered: Customer Support (even on Sundays), Live Training, Desktop+Mobile access
          </p>
        </div>

        {/* Price and Button */}
        <div>
          {/* Flash Sale or Special Offer Label */}
          {(edition.flashOfferOn || edition.showD1Offer) && (
            <div className="mb-2 bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded text-[12px] font-semibold">
              {priceDisplay.offerLabel}
            </div>
          )}

          {/* DESKTOP LAYOUT */}
          {(showYY !== false) && (
            <ConditionalDiv className="hidden md:block mb-3">
              {!flashOfferOn && !sundayOfferOn && !hasGenericOffer &&
                <div className="flex items-end justify-between gap-4 w-full">
                  <div>
                    {priceDisplay.showStrikethrough && priceDisplay.strikethroughPrice && (
                      <div className="mb-2 flex items-baseline gap-2 text-sm text-text-primary">Regular Rate:
                        <span className="text-sm text-text-secondary">
                          <span className='text-[11px]'>₹ </span><span className='font-bold'>{priceDisplay.strikethroughPrice} </span><span className='text-[11px]'>/ year</span>
                        </span>
                      </div>
                    )}
                    {priceDisplay.showCutthrough && priceDisplay.cutPrice && (
                      <div className="text-sm text-text-secondary mb-1">
                        <span className="font-semibold">Regular Price:</span>{' '}
                        <span className="">₹ {priceDisplay.cutPrice}</span>
                      </div>
                    )}
                    <div className="flex items-baseline gap-1">
                      <span className="text-lg text-text-secondary">₹</span>
                      <span className="text-[28px] font-extrabold text-text-primary">
                        {priceDisplay.mainPrice}
                      </span>
                      <span className="text-[15px] text-text-secondary">/ year</span>
                      <span className="text-sm text-primary font-bold">
                        ({priceDisplay.discountPercent}% off)
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-end">
                    <Button
                      onClick={() => { handleBuyNowWithDefaultCoupon(edition.id); onPackageClick?.(edition.id); }}
                      variant="primary"
                      className="w-auto"
                    >
                      Buy Now
                    </Button>
                  </div>
                </div>}

              {/* DESKTOP FLASH OFFER BOX */}
              {flashOfferOn && (
                <div className="border-2 border-primary bg-primary-subtle rounded p-3">
                  <div className="flex items-end justify-between gap-4 w-full">
                    <div>
                      <p className="m-0 font-bold text-primary text-[19px] mb-1">Flash Offer</p>
                      <p className="text-[15px] font-semibold text-text-primary mb-1">{FLASHOFFER.tagline}</p>
                      <div className="flex items-baseline gap-1">
                        <span className="text-[11px] text-text-secondary">₹</span>
                        <span className="text-sm font-bold text-text-secondary line-through">{offerPrice.stdPrice}</span>
                        <span className="text-[18px] text-text-secondary ml-1">₹</span>
                        <span className="text-[28px] font-extrabold text-text-primary">{offerPrice.displayPrice}</span>
                        <span className="text-[15px] text-text-secondary">+GST</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-end">
                      <Button
                        onClick={() => { handleUseCoupon('FLASHOFFER', edition.id, 'YY'); onPackageClick?.(edition.id); }}
                        variant="primary"
                        className="w-auto"
                      >
                        Buy Now
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* DESKTOP SUNDAY OFFER BOX */}
              {sundayOfferOn && (
                <div className="border-2 border-primary bg-primary-subtle rounded p-3">
                  <div className="flex items-end justify-between gap-4 w-full">
                    <div>
                      <p className="m-0 font-bold text-primary text-[19px] mb-1">Sunday Special</p>
                      <p className="text-[15px] font-semibold text-text-primary mb-1">Extra {Math.round((1 - Number(offerPrice.displayPrice.replace(/,/g, '')) / Number(offerPrice.stdPrice.replace(/,/g, ''))) * 100)}% off on first year</p>
                      <div className="flex items-baseline gap-1">
                        <span className="text-[11px] text-text-secondary">₹</span>
                        <span className="text-sm font-bold text-text-secondary line-through">{offerPrice.stdPrice}</span>
                        <span className="text-[18px] text-text-secondary ml-1">₹</span>
                        <span className="text-[28px] font-extrabold text-text-primary">{offerPrice.displayPrice}</span>
                        <span className="text-[15px] text-text-secondary">+GST</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-end">
                      <Button
                        onClick={() => { handleUseCoupon('SUNSPL30', edition.id, 'YY'); onPackageClick?.(edition.id); }}
                        variant="primary"
                        className="w-auto"
                      >
                        Buy Now
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* DESKTOP GENERIC OFFER BOX — D3, OLD_PL, FDBK_CLRNC, etc. */}
              {hasGenericOffer && (
                <div className="border-2 border-primary bg-primary-subtle rounded p-3">
                  <div className="flex items-end justify-between gap-4 w-full">
                    <div>
                      <p className="m-0 font-bold text-primary text-[19px] mb-1">Limited Time Offer</p>
                      <p className="text-[15px] font-semibold text-text-primary mb-1">Extra {Math.round((1 - Number(offerPrice.displayPrice.replace(/,/g, '')) / Number(offerPrice.stdPrice.replace(/,/g, ''))) * 100)}% off for First Year — Today Only!</p>
                      <div className="flex items-baseline gap-1">
                        <span className="text-[11px] text-text-secondary">₹</span>
                        <span className="text-sm font-bold text-text-secondary line-through">{offerPrice.stdPrice}</span>
                        <span className="text-[18px] text-text-secondary ml-1">₹</span>
                        <span className="text-[28px] font-extrabold text-text-primary">{offerPrice.displayPrice}</span>
                        <span className="text-[15px] text-text-secondary">+GST</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-end">
                      <Button
                        onClick={() => { handleUseCoupon(activeOffer.couponCode, edition.id, activeOffer.planPeriod); onPackageClick?.(edition.id); }}
                        variant="primary"
                        className="w-auto"
                      >
                        Buy Now
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </ConditionalDiv>
          )}


          {/* MOBILE LAYOUT */}
          {(showYY !== false) && (
            <div className={`md:hidden bg-background-light border-2 border-border rounded p-3 ${flashOfferOn || sundayOfferOn ? 'mb-4' : 'mb-0'}`}>
              {/* Regular Rate - Strikethrough */}
              {priceDisplay.showStrikethrough && priceDisplay.strikethroughPrice && (
                <div className="mb-2">
                  <span className="text-xs text-text-primary">Regular Rate: </span>
                  <span className="text-sm font-bold text-text-secondary"><span className='text-[11px]'>₹ </span>{priceDisplay.strikethroughPrice}<span className='text-[11px]'> / year</span></span>
                </div>
              )}
              {priceDisplay.showCutthrough && priceDisplay.cutPrice && (
                <div className="mb-2">
                  <span className="text-xs text-text-primary">Regular Price: </span>
                  <span className="text-sm font-bold text-text-secondary"><span className='text-[11px]'>₹ </span>{priceDisplay.cutPrice}<span className='text-[11px]'> / year</span></span>
                </div>
              )}

              {/* Offer Rate - Main Price Display */}
              <div className="mb-3">
                <div className="flex items-baseline gap-1 flex-wrap">
                  <span className="text-xs text-text-primary">Offer Rate:</span>
                  <span className="text-sm text-text-secondary">₹</span>
                  <span className="text-2xl font-extrabold text-text-primary">{priceDisplay.mainPrice}</span>
                  <span className="text-xs text-text-secondary">/ year</span>
                  {priceDisplay.discountPercent > 0 && (
                    <span className="text-sm font-bold text-primary">
                      ({priceDisplay.discountPercent}% off)
                    </span>
                  )}
                </div>
              </div>

              {/* <p className="text-xs text-text-secondary mb-4">(GST extra. Limited time offer.)</p> */}

              {/* Buy Now Button */}
              <Button
                onClick={() => { handleBuyNowWithDefaultCoupon(edition.id); onPackageClick?.(edition.id); }}
                variant="primary"
                className="w-full"
              >
                Buy Now
              </Button>
            </div>
          )}
          {/* MOBILE FLASH OFFER BOX */}
          {(showYY !== false) && flashOfferOn && (
            <div className="md:hidden mb-3 border-2 border-primary bg-primary-subtle rounded p-3">
              <h2 className="m-0 font-bold text-primary text-[19px] md:text-[28px]">Flash Offer</h2>
              <p className="text-[15px] font-semibold mb-2 text-text-primary mt-2">{FLASHOFFER.tagline}</p>
              <div className="flex items-baseline gap-1 flex-wrap mb-3">
                <span className="text-[11px] text-text-secondary">₹</span>
                <span className="text-sm font-bold text-text-secondary line-through">{offerPrice.stdPrice}</span>
                <span className="text-[14px] text-text-secondary ml-1">₹</span>
                <span className="text-2xl font-extrabold text-text-primary">{offerPrice.displayPrice}</span>
                <span className="text-xs text-text-secondary">+GST</span>
              </div>
              <Button
                onClick={() => { handleUseCoupon('FLASHOFFER', edition.id, 'YY'); onPackageClick?.(edition.id); }}
                variant="primary"
                className="w-full"
              >
                Get Discount Offer
              </Button>
            </div>
          )}
          {/* MOBILE SUNDAY OFFER BOX */}
          {(showYY !== false) && sundayOfferOn && (
            <div className="md:hidden mb-3 border-2 border-primary bg-primary-subtle rounded p-3">
              <h2 className="m-0 font-bold text-primary text-[19px] md:text-[28px]">Sunday Special</h2>
              <p className="text-[15px] font-semibold mb-2 text-text-primary mt-2">Extra {Math.round((1 - Number(offerPrice.displayPrice.replace(/,/g, '')) / Number(offerPrice.stdPrice.replace(/,/g, ''))) * 100)}% off on first year</p>
              <div className="flex items-baseline gap-1 flex-wrap mb-3">
                <span className="text-[11px] text-text-secondary">₹</span>
                <span className="text-sm font-bold text-text-secondary line-through">{offerPrice.stdPrice}</span>
                <span className="text-[14px] text-text-secondary ml-1">₹</span>
                <span className="text-2xl font-extrabold text-text-primary">{offerPrice.displayPrice}</span>
                <span className="text-xs text-text-secondary">+GST</span>
              </div>
              <Button
                onClick={() => { handleUseCoupon('SUNSPL30', edition.id, 'YY'); onPackageClick?.(edition.id); }}
                variant="primary"
                className="w-full"
              >
                Buy Now
              </Button>
            </div>
          )}


          {/* OR DIVIDER - Show only when both yearly and monthly are displayed */}
          {showBothPrices && (
            <div className="flex items-center gap-2 my-3">
              <div className="flex-1 border-t border-border"></div>
              <span className="text-sm font-semibold text-text-secondary px-2">OR</span>
              <div className="flex-1 border-t border-border"></div>
            </div>
          )}

          {/* MONTHLY PRICING SECTION */}
          {showBothPrices && monthlyDisplay && (
            <div className="mb-4">
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-lg text-text-primary">₹ </span>
                <span className={`text-[28px] font-extrabold text-text-primary
                  }`}>
                  {monthlyDisplay.mainPrice}
                </span>
                <span className="text-[15px]"> / month</span>

                {/* Discount Badge for Monthly */}
                {monthlyDisplay.discountPercent > 0 && (
                  <b className="text-primary text-[15px] ml-1">({monthlyDisplay.discountPercent}% off)</b>
                )}
              </div>
            </div>
          )}

          {/* View Discount Coupons Button or Expanded Coupons */}
          {showCoupons && availableCoupons.length > 0 && (
            <>
              {!isCpnExpanded ? (
                <button
                  onClick={() => { handleViewCoupons(edition); onCouponView?.(edition.name); }}
                  className="w-full mt-3 md:mt-0 bg-primary text-white font-semibold py-2 px-4 rounded border transition-colors flex items-center justify-between"
                >
                  <span>View Discount Coupons</span>
                  <span>›</span>
                </button>
              ) : (
                <div className="mt-3 md:mt-0 space-y-2">
                  <h3 className="text-base font-bold text-text-primary mb-2">Available Discount Coupons:</h3>
                  {availableCoupons.map((coupon) => (
                    <div key={coupon.id} className="border-2 bg-white border-border rounded p-3">
                      <h4 className="text-sm md:text-base font-bold text-text-primary mb-1">{coupon.title}</h4>
                      <p className="text-xs md:text-sm text-text-secondary mb-3">
                        {coupon.description} using coupon code: {' '}
                        <span className="font-mono font-bold text-primary">{coupon.code}</span>
                      </p>
                      <Button
                        onClick={() => handleUseCoupon(coupon.code, edition.id)}
                        size="sm"
                      >
                        <i className="fa fa-tag mr-2"></i> Get Offer
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div >
    );
  };

  const pricingRef = React.useRef(null);
  React.useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { onPackageView?.(); observer.disconnect(); }
    }, { threshold: 0.3 });
    if (pricingRef.current) observer.observe(pricingRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={pricingRef} id="simplePricing" className={`border-top ${!isMobile ? 'bg-background-light' : 'bg-white'} ${isFirstSection && !isMobile ? 'py-6 md:py-8 lg:py-10' : 'py-12 md:py-16 lg:py-20'} text-center `}>
      {/* ========== DESKTOP VIEW ========== */}
      <Container variant="wide" className="hidden md:block">
        {/* Main Heading */}
        <h1 className="text-[40px] font-bold secttl mb-0 px-10 pb-3">
          Unbeatable Pricing
        </h1>
        {pageName?.toUpperCase() === 'GERP' && <p className="text-[14px] md:text-lg text-[#808080] px-2 mb-6">All Features & Integrations Included</p>}
        {/* Page-specific description */}
        {pageName && PAGE_EDITION_MAPPER[pageName.toLowerCase()] && (
          <ConditionalDiv className="text-lg text-text-secondary mb-8 max-w-3xl mx-auto">
            {PAGE_EDITION_MAPPER[pageName.toLowerCase()].description}
          </ConditionalDiv>
        )}

        {visibleEditions.length === 1 ? (
          /* ── SINGLE EDITION: Two-column layout ── */
          (() => {
            const singleEdition = visibleEditions[0];
            const priceDisplay = getDisplayPrice(singleEdition);
            const singleOfferPrice = getEditionPricing(singleEdition, activeOffer);

            const handleDesktopBuyNow = () => {
              if (singleOfferPrice.isBogo) { handleUseCoupon(activeOffer.couponCode, singleEdition.id, 'YY2'); }
              else if (singleOfferPrice.isOffer) { handleUseCoupon(activeOffer.couponCode, singleEdition.id, activeOffer.planPeriod); }
              else { handleBuyNowWithDefaultCoupon(singleEdition.id); }
              onPackageClick?.(singleEdition.id);
            };
            return (
              <div className="max-w-7xl mx-auto flex justify-center">
                <style>{`
                    @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
                    @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
                    @keyframes pulse-ring { 0% { box-shadow: 0 0 0 0 rgba(var(--color-primary-rgb, 79,70,229), 0.25); } 70% { box-shadow: 0 0 0 12px rgba(var(--color-primary-rgb, 79,70,229), 0); } 100% { box-shadow: 0 0 0 0 rgba(var(--color-primary-rgb, 79,70,229), 0); } }
                  `}</style>

                <div className="overflow-hidden w-full max-w-4xl">
                  <div className="px-8 pt-8 flex flex-col items-center text-center gap-6">

                    {/* Headline */}
                    <div>
                      <p className="text-2xl font-bold text-text-secondary mb-2">All Features & Integrations mentioned above @ just</p>
                    </div>

                    {/* Price block */}
                    <div className="flex flex-col items-center gap-1">

                      {/* Strikethrough std price — always default edition price, not offer price */}
                      {priceDisplay.strikethroughPrice && (
                        <div className="flex items-baseline gap-1 text-text-secondary opacity-60">
                          <span className="text-3xl mr-1 font-semibold opacity-60">₹</span>
                          <span className="text-5xl line-through font-bold">{priceDisplay.strikethroughPrice}</span>
                        </div>
                      )}

                      {/* Main price — always default edition price */}
                      <div className="flex items-end gap-1 mt-4 leading-none">
                        <span className="text-[40px] mr-1 text-primary font-semibold mb-1 opacity-60">₹</span>
                        <span className="text-[72px] font-extrabold text-primary leading-none tracking-tight">{priceDisplay.mainPrice}</span>
                      </div>
                      <p className="text-[20px] text-text-secondary mt-1">per year <span className="font-medium"> +GST</span></p>
                    </div>
                    {/* 
                    Discount badge
                    {!singleOfferPrice.isBogo && flashOfferOn && (
                      <div className="text-primary text-2xl font-bold px-5 py-1.5" style={{ animation: 'blink 2s ease-out infinite' }}>
                        Flat <span className="text-xl opacity-60 text-black">₹</span> {(Number((singleOfferPrice.strikePrice).replace(/,/g, '')) - Number(singleOfferPrice.mainPrice.replace(/,/g, ''))).toLocaleString('en-IN')} OFF!
                      </div>
                    )} */}

                    {singleOfferPrice.discountPercent > 0 && !singleOfferPrice.isOffer && !singleOfferPrice.isBogo ? (
                      <div className="border border-primary bg-primary-subtle text-primary text-xl font-bold px-4 py-2 rounded" style={{ animation: 'blink 2s ease-in-out infinite' }}>
                        {/* {singleOfferPrice.discountPercent}% OFF! */}
                        Get {singleOfferPrice.discountPercent}% OFF on Our Anniversary Offer!
                      </div>
                    ) : null}

                    {/* Value props */}
                    <div className="w-full px-6 py-4 flex flex-row justify-center items-center gap-10 text-[14px] text-text-primary">
                      <div className="flex items-center gap-2 justify-center text-xl">
                        <span className="text-primary font-bold">+</span>
                        <span><strong>Free Support</strong></span>
                      </div>
                      <div className="flex items-center gap-2 justify-center text-xl">
                        <span className="text-primary font-bold">+</span>
                        <span><strong>Unlimited User Access</strong> (Desktop + Mobile)</span>
                      </div>
                      {singleEdition.rfdAvl && (
                        <div className="flex items-center gap-2 justify-center text-xl">
                          <span className="text-primary font-bold">+</span>
                          <span>7-Day Money-Back Guarantee</span>
                        </div>
                      )}
                    </div>

                    {/* CTA buttons */}
                    <div className="flex flex-col sm:flex-row justify-center items-center gap-3 w-full mt-1 max-w-[400px]">
                      <Button onClick={() => { onBuyNow?.(singleEdition.id); onPackageClick?.(singleEdition.id); }} className="flex-1 text-base py-3 max-w-[200px]">
                        Buy Now
                      </Button>
                      {getCouponsByEdition(singleEdition.id).length > 0 && !singleOfferPrice.isOffer && !isFromWebsite && (
                        <Button
                          onClick={() => { handleViewCoupons(singleEdition); onCouponView?.(singleEdition.name); }}
                          variant="secondary"
                          className="flex-1 text-base py-3 bg-primary-subtle hover:bg-primary-subtle"
                        >
                          Check Offers
                        </Button>
                      )}
                    </div>

                    {/* Offer card — desktop single edition */}
                    {(() => {
                      const hasAnyOfferHere = !!activeOffer && activeOffer?.offerKey !== 'FRESH';
                      if (!hasAnyOfferHere) return null;
                      const isBogoOffer = singleOfferPrice.isBogo;
                      const offerColor = activeBrand === 'bz' ? '#0b5d1e' : '#e88421';
                      const offerBg = activeBrand === 'bz' ? '#0b5d1e14' : '#fff7ed';
                      const handleDesktopOfrClick = () => {
                        if (isBogoOffer) { handleUseCoupon(activeOffer.couponCode, singleEdition.id, 'YY2'); }
                        else if (singleOfferPrice.isOffer) { handleUseCoupon(activeOffer.couponCode, singleEdition.id, activeOffer.planPeriod); }
                        else { handleBuyNowWithDefaultCoupon(singleEdition.id); }
                        onPackageClick?.(singleEdition.id);
                      };
                      return (
                        <>
                          <style>{`@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>

                          {isBogoOffer ? (
                            <div
                              className="rounded mt-2 cursor-pointer px-6 py-4 max-w-xl w-full mx-auto"
                              style={{ border: `1px solid ${offerColor}`, backgroundColor: offerBg }}
                            >
                              <div className="flex items-end justify-between gap-6">
                                <div>
                                  <p className="m-0 font-bold text-[26px] mb-1" style={{ color: offerColor }}>BOGO Offer</p>
                                  <p className=" text-text-primary mb-1 text-[18px]">Buy One year Get One year extra!</p>
                                  <div className="text-center items-baseline">
                                    <span className="text-[18px] text-text-secondary ml-1">₹</span>
                                    <span className="text-[40px] font-bold ml-1" style={{ color: offerColor }}>{singleOfferPrice.mainPrice}</span>
                                    <span className="text-[15px] text-text-secondary ml-2">+GST</span>
                                  </div>
                                  <p className="m-0 text-text-secondary  mt-1">for <span style={{ textDecoration: 'line-through' }}>One</span> Two years</p>
                                </div>
                                <div className="flex flex-col items-center gap-2">
                                  <p className="m-0 font-semibold text-red-500 mb-1" style={{ animation: 'blink 1.5s step-start infinite' }}>Offer Ending Soon!</p>

                                  <Button
                                    size="lg"
                                    onClick={(e) => { e.stopPropagation(); handleDesktopOfrClick(); }}
                                    variant="primaryDarkBlue"
                                  >
                                    Buy Now
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div
                              className="rounded mt-2 cursor-pointer px-6 py-4 max-w-lg w-full mx-auto"
                              style={{ border: `2px solid ${offerColor}`, backgroundColor: offerBg }}
                            >
                              <div className="flex items-end justify-between gap-6">
                                <div>
                                  <p className="m-0 font-bold text-[26px] mb-1" style={{ color: offerColor }}>{singleOfferPrice?.offerLabel || 'Special Offer'}</p>
                                  <p className=" text-text-primary mb-1 text-[18px]">Start First year @ just</p>
                                  <div className="flex items-baseline gap-1">

                                    <span className="text-[18px] text-text-secondary ml-1">₹</span>
                                    <span className="text-[40px] font-bold" style={{ color: offerColor }}>{singleOfferPrice.mainPrice}</span>
                                    <span className="text-[15px] text-text-secondary">+GST</span>
                                  </div>
                                </div>
                                <div className="flex flex-col items-center gap-2">
                                  <p className="m-0font-semibold text-red-500 mb-1" style={{ animation: 'blink 1.5s step-start infinite' }}>Offer Ending Soon!</p>

                                  <Button
                                    size="lg"
                                    onClick={(e) => { e.stopPropagation(); handleDesktopOfrClick(); }}
                                    variant="primaryDarkBlue"
                                  >
                                    Buy Now
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}

                  </div>
                </div>
              </div>
            );
          })()
        ) : (
          /* ── MULTIPLE EDITIONS: Original layout ── */
          <>

            {/* Pricing Cards */}
            <div className="flex flex-row gap-8 justify-center max-w-7xl mx-auto">

              {visibleEditions.map(edition => {
                const priceDisplay = getDisplayPrice(edition);
                const edOfferPrice = getOfferPrice(edition, activeOffer);
                const hasOffer = flashOfferOn || sundayOfferOn || hasGenericOffer;
                const showStdPrice = hasOffer ? edOfferPrice.stdPrice : priceDisplay.strikethroughPrice;
                const showRnwlPrice = hasOffer ? edOfferPrice.rnwlPrice : priceDisplay.strikethroughPrice;
                const showOfferPrice = hasOffer ? edOfferPrice.displayPrice : priceDisplay.mainPrice;
                const discPct = hasOffer
                  ? Math.round((1 - Number(edOfferPrice.displayPrice.replace(/,/g, '')) / Number(edOfferPrice.stdPrice.replace(/,/g, ''))) * 100)
                  : priceDisplay.discountPercent;
                const handleCardBuyNow = () => {
                  if (hasOffer) { handleUseCoupon(activeOffer.couponCode, edition.id, activeOffer.planPeriod); }
                  else { handleBuyNowWithDefaultCoupon(edition.id); }
                  onPackageClick?.(edition.id);
                };
                return (
                  <div key={edition.id} className="flex-1 min-w-[300px] max-w-[450px] bg-white border border-border rounded p-8 text-center flex flex-col shadow-[0_2px_5px_rgba(0,0,0,0.05)]">
                    <style>{`@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>
                    <h2 className="text-[27px] font-bold text-text-primary mt-0 mb-1">{edition.name}</h2>
                    <p className="text-[15px] text-text-primary mb-6 italic">{edition.tagline || 'Complete business solution for your enterprise'}</p>

                    {pageName?.toUpperCase() === 'GERP' && <p className="text-sm text-text-secondary mb-2">Regular Rate: <span className="text-[11px]">₹</span> <span className="text-xl font-bold ">{showStdPrice}</span> <span className="text-xs">/ year +GST</span></p>}
                    {pageName?.toUpperCase() === 'GERP' && <p className="text-xl font-bold text-text-secondary mb-2">Start 1st year @ just</p>}
                    <div className="flex flex-col items-center gap-1 mb-4">
                      {showStdPrice && (
                        <div className="flex items-baseline gap-1 text-text-secondary opacity-60">
                          <span className="text-xl mr-1 font-semibold">₹</span>
                          <span className="text-3xl line-through font-bold">{hasOffer ? showRnwlPrice : showStdPrice}</span>
                        </div>
                      )}
                      <div className="flex items-end gap-1 mt-2 leading-none">
                        <span className="text-[28px] mr-1 text-primary font-semibold mb-1 opacity-60">₹</span>
                        <span className="text-[56px] font-extrabold text-primary leading-none tracking-tight">{showOfferPrice}</span>
                      </div>

                    </div>

                    {flashOfferOn && (
                      <div className="text-primary text-xl font-bold px-5 py-1.5 mb-2" style={{ animation: 'blink 2s ease-out infinite' }}>
                        Flat <span className="text-lg opacity-60 text-black">₹</span> {(Number((hasOffer ? showRnwlPrice : showStdPrice).replace(/,/g, '')) - Number(showOfferPrice.replace(/,/g, ''))).toLocaleString('en-IN')} OFF!
                      </div>
                    )}
                    {discPct > 0 && !flashOfferOn && (
                      <div className="text-primary text-xl font-bold px-5 py-1.5 mb-2" style={{ animation: 'blink 2s ease-in-out infinite' }}>
                        {discPct}% OFF!
                      </div>
                    )}

                    <div className="flex flex-col items-center gap-2 text-[14px] my-4 text-text-primary">
                      {edition.pricingLines && edition.pricingLines.length > 0 ? (
                        edition.pricingLines.map((line, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-base">
                            <span className="text-primary font-bold">+</span>
                            <span><strong>{line}</strong></span>
                          </div>
                        ))
                      ) : (
                        <>
                          <div className="flex items-center gap-2 text-base">
                            <span className="text-primary font-bold">+</span>
                            <span><strong>Free Support</strong></span>
                          </div>
                          <div className="flex items-center gap-2 text-base">
                            <span className="text-primary font-bold">+</span>
                            <span><strong>Unlimited User Access</strong> (Desktop + Mobile)</span>
                          </div>
                        </>
                      )}
                      {edition.rfdAvl && (
                        <div className="flex items-center gap-2 text-base">
                          <span className="text-primary font-bold">+</span>
                          <span>7-Day Money-Back Guarantee</span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row justify-center items-center gap-3 mt-4">
                      <Button onClick={handleCardBuyNow} className="flex-1 text-base py-3 max-w-[200px]">
                        Buy Now
                      </Button>
                      {getCouponsByEdition(edition.id).length > 0 && !hasOffer && !isFromWebsite && (
                        <Button
                          onClick={() => { handleViewCoupons(edition); onCouponView?.(edition.name); }}
                          variant="secondary"
                          className="flex-1 text-base py-3 bg-primary-subtle hover:bg-primary-subtle"
                        >
                          Check Offers
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* <p className="text-[14px] text-text-secondary mt-6">(GST extra. Limited time offer.)</p> */}

            <ConditionalDiv className="mt-10 text-text-primary">
              {visibleEditions.some(e => e.rfdAvl) && (
                <p className="font-bold text-lg">
                  Each package is offered with 7-day Moneyback Guarantee
                </p>
              )}
            </ConditionalDiv>
          </>
        )}
      </Container>

      {/* ========== MOBILE VIEW ========== */}
      <Container className="md:hidden">
        {/* Main Heading */}
        <h1 className="text-[30px] font-bold secttl mb-0 px-4 pb-6">
          Unbeatable Pricing
        </h1>

        {/* Page-specific description */}
        {pageName && PAGE_EDITION_MAPPER[pageName.toLowerCase()] && (
          <ConditionalDiv className="text-lg text-text-secondary mb-8 max-w-3xl mx-auto">
            {PAGE_EDITION_MAPPER[pageName.toLowerCase()].description}
          </ConditionalDiv>
        )}

        {/* MOBILE: QikERP brand — single unified card with included list + silver price */}
        {visibleEditions.length === 1 ? (
          <>
            {/* <div className="mb-5 text-left px-4">
              <h4 className="font-bold  text-text-primary mb-1">
                What's included:
              </h4>
            </div> */}
            <div className="mb-8 px-4">
              <div>
                <p className="text-[1.2rem] md:text-2xl font-bold text-text-secondary mb-2">All Features & Integrations mentioned above @ just</p>
              </div>

            </div>
            {(() => {
              const silverEdition = visibleEditions[0];
              if (!silverEdition) return null;
              const priceDisplay = getDisplayPrice(silverEdition);
              const silverOfferPrice = getOfferPrice(silverEdition, activeOffer);
              const discPct = (flashOfferOn || sundayOfferOn || hasGenericOffer)
                ? Math.round((1 - Number(silverOfferPrice.displayPrice.replace(/,/g, '')) / Number(silverOfferPrice.stdPrice.replace(/,/g, ''))) * 100)
                : priceDisplay.discountPercent;
              const showStdPrice = (flashOfferOn || sundayOfferOn || hasGenericOffer) ? silverOfferPrice.stdPrice : priceDisplay.strikethroughPrice;
              const showRnwlPrice = (flashOfferOn || sundayOfferOn || hasGenericOffer) ? silverOfferPrice.rnwlPrice : priceDisplay.strikethroughPrice;
              const showOfferPrice = (flashOfferOn || sundayOfferOn || hasGenericOffer) ? silverOfferPrice.displayPrice : priceDisplay.mainPrice;
              const hasOffer = (flashOfferOn || sundayOfferOn || hasGenericOffer);
              const isBogoOffer = getEditionPricing(silverEdition, activeOffer)?.isBogo ?? false;
              const handleMobileBuyNow = () => {
                // if (isBogoOffer) { handleUseCoupon(activeOffer.couponCode, silverEdition.id, 'YY2'); }
                // else if (flashOfferOn || sundayOfferOn || hasGenericOffer) { handleUseCoupon(activeOffer.couponCode, silverEdition.id, activeOffer.planPeriod); }
                //  handleBuyNowWithDefaultCoupon(silverEdition.id); 
                onBuyNow?.(silverEdition.id);
              };

              const handleMobileOfrClick = () => {
                if (isBogoOffer) {
                  handleUseCoupon(activeOffer.couponCode, silverEdition.id, 'YY2');
                } else if (flashOfferOn || sundayOfferOn || hasGenericOffer) {
                  handleUseCoupon(activeOffer.couponCode, silverEdition.id, activeOffer.planPeriod);
                } else {
                  handleBuyNowWithDefaultCoupon(silverEdition.id);
                }
              }
              return (
                <div className="px-4 flex flex-col gap-3">
                  <div className="text-center flex flex-col ">
                    <style>{`@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>

                    {/* Price block */}
                    <div className="flex flex-col items-center gap-1 mb-4">

                      {/* Strikethrough std price */}
                      {showStdPrice && (
                        <div className="flex items-baseline gap-1 text-text-secondary opacity-60">
                          <span className="text-3xl mr-1 font-semibold opacity-60">₹</span>
                          {/* <span className="text-5xl line-through font-bold">{isBogoOffer ? showStdPrice : (hasOffer ? showRnwlPrice : showStdPrice)}</span> */}
                          <span className="text-5xl line-through font-bold">{priceDisplay.strikethroughPrice}</span>
                          {/* <span className="text-2xl">/yr</span> */}
                        </div>
                      )}

                      {/* Offer price — hero */}
                      <div className="flex items-end gap-1 mt-4 leading-none">
                        <span className="text-[40px] mr-1 text-primary font-semibold mb-1 opacity-60">₹</span>
                        <span className="text-[72px] font-extrabold text-primary leading-none tracking-tight">{priceDisplay.mainPrice}</span>
                      </div>
                      <p className="text-[20px] text-text-secondary mt-1">per year +GST</p>
                      {/* <p className="text-[20px] text-text-secondary mt-1">{isBogoOffer ? 'for 2 years +GST' : hasOffer ? 'for 1st year +GST' : 'per year +GST'}</p> */}
                    </div>


                    {/* Value props */}
                    <div className="w-full flex flex-col items-center gap-2 text-[14px] my-8 text-text-primary">

                      <div className="flex items-start text-center gap-2 text-xl leading-0l">
                        <span className="text-primary font-bold text-2xl leading-0 -translate-y-1">+</span>
                        <span><strong>Free Support</strong></span>
                      </div>

                      <div className="flex items-start text-center gap-2 text-xl  leading-0">
                        <span className="text-primary font-bold text-2xl leading-0 -translate-y-1">+</span>
                        <span><strong>Unlimited User Access</strong> <br></br>(Desktop + Mobile)</span>
                      </div>

                    </div>

                    <div className="mx-7 mt-3 mb-5 flex flex-col gap-3 items-center">
                      <Button
                        onClick={handleMobileBuyNow}
                        className="w-full mobile-btn"
                      >
                        Buy Now
                      </Button>
                      {(getCouponsByEdition(silverEdition.id).length > 0 && !hasOffer && !isFromWebsite) &&
                        <Button
                          onClick={() => { handleViewCoupons(silverEdition); onCouponView?.(silverEdition.name); }}
                          variant="secondary"
                          className="w-full mobile-btn bg-primary-subtle hover:bg-primary-subtle"
                        >
                          Check Offers
                        </Button>}
                    </div>
                    {/* Offer Cards — mobile single edition */}
                    {(() => {
                      const edOfferPrice = getEditionPricing(silverEdition, activeOffer);
                      if (!edOfferPrice) return null;
                      const isBogoOffer = edOfferPrice.isBogo;
                      const hasAnyOfferHere = !!activeOffer && activeOffer?.offerKey !== 'FRESH';
                      if (!hasAnyOfferHere) return null;
                      return (
                        <>
                          <style>{`@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>
                          <div
                            className="rounded cursor-pointer px-4 py-3 flex flex-col gap-2 mb-2 w-[230px] mx-auto"
                            style={{
                              border: `1.5px solid ${activeBrand === 'bz' ? '#0b5d1e' : '#e88421'}`,
                              backgroundColor: activeBrand === 'bz' ? '#0b5d1e14' : '#fff7ed',
                            }}
                            onClick={() => handleMobileBuyNow()}
                          >
                            <p className="m-0 font-bold text-[22px]" style={{ color: activeBrand === 'bz' ? '#0b5d1e' : '#e88421' }}>{isBogoOffer ? 'BOGO Offer' : edOfferPrice?.offerLabel || 'Special Offer'}</p>
                            {isBogoOffer ? (
                              <>
                                {/* <p className="m-0 text-text-primary leading-normal">Get Extra Discount!</p> */}
                                <p className="m-0 text-text-primary mt-1">Buy One year Get One year extra!</p>
                                <div className="flex flex-col items-center gap-0">
                                  <div className="flex items-baseline gap-1">
                                    <span className="text-[20px] text-text-secondary">₹</span>
                                    <span className={`text-[40px] font-bold ${activeBrand === 'bz' ? 'text-[#0b5d1e]' : 'text-[#e88421]'}`}>{edOfferPrice.mainPrice}</span>
                                    <span className="text-xs text-text-secondary">+GST</span>
                                  </div>
                                </div>
                                <p className="m-0 text-text-secondary text-sm">for <span className="line-through">One</span> Two years</p>
                              </>
                            ) : (
                              <>
                                <p className="m-0 text-text-primary leading-normal">Get Extra Discount!</p>
                                <p className="m-0 text-text-primary mt-1">Start First year @ just</p>
                                <div className="flex flex-col items-center gap-0">
                                  <div>
                                    <span className="text-text-secondary">₹ </span>
                                    <span className="text-text-secondary line-through">{edOfferPrice.strikePrice}</span>
                                  </div>
                                  <div className="flex items-baseline gap-1">
                                    <span className="text-[20px] text-text-secondary">₹</span>
                                    <span className={`text-[40px] font-bold ${activeBrand === 'bz' ? 'text-[#0b5d1e]' : 'text-[#e88421]'}`}>{edOfferPrice.mainPrice}</span>
                                    <span className="text-xs text-text-secondary">+GST</span>
                                  </div>
                                </div>
                              </>
                            )}
                            <Button
                              onClick={(e) => { e.stopPropagation(); handleMobileOfrClick(); }}
                              variant="primaryDarkBlue"
                            >
                              Buy Now
                            </Button>

                            <p className="m-0 mt-2 mb-1 text-sm font-semibold text-center text-red-500" style={{ animation: 'blink 1.5s step-start infinite' }}>Offer Ending Soon!</p>

                          </div>
                        </>
                      );
                    })()}

                    {/* {flashOfferOn && (
                      <p className="text-lg font-bold text-primary mb-1 mt-3">
                        Flat <span className="text-xs text-text-secondary">₹</span> {(Number(showStdPrice.replace(/,/g, '')) - Number(showOfferPrice.replace(/,/g, ''))).toLocaleString('en-IN')} OFF!
                      </p>
                    )}
                    {discPct > 0 && !hasOffer && (
                      <p className="text-lg font-bold text-primary mt-2" style={{ animation: 'blink 1.2s step-start infinite' }}>
                        {discPct}% OFF!
                      </p>
                    )} */}
                    {/* {!isBogoOffer && flashOfferOn && (
                      <div className="text-primary text-2xl font-bold px-5 py-1.5" style={{ animation: 'blink 2s ease-out infinite' }}>
                        Flat <span className="text-xl opacity-60 text-black">₹</span> {(Number((hasOffer ? showRnwlPrice : showStdPrice).replace(/,/g, '')) - Number(showOfferPrice.replace(/,/g, ''))).toLocaleString('en-IN')} OFF!
                      </div>
                    )} */}
                    {/* {isBogoOffer && !flashOfferOn && (
                      <div className="text-primary text-2xl font-bold px-5 py-1.5" style={{ animation: 'blink 2s ease-out infinite' }}>
                        BOGO Offer!
                      </div>
                    )} */}
                    {discPct > 0 && !hasOffer && (
                      <div
                        className="mt-6 mx-7 px-4 py-3 border border-primary bg-primary-subtle text-primary rounded flex flex-col items-center justify-center text-center"
                        style={{ animation: 'blink 2s ease-in-out infinite' }} onClick={handleMobileBuyNow}
                      >
                        <div className="text-xl font-bold uppercase tracking-wide">
                          Anniversary Offer
                        </div>
                        <div className=" font-medium mt-1">
                          Get {discPct}% OFF!
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </>
        ) : (
          <>
            {/* MOBILE: Hero-style cards stacked */}
            <div className="flex flex-col gap-6 justify-center max-w-4xl mx-auto px-4">
              {visibleEditions.map(edition => {
                const priceDisplay = getDisplayPrice(edition);
                const edOfferPrice = getOfferPrice(edition, activeOffer);
                const hasOffer = flashOfferOn || sundayOfferOn || hasGenericOffer;
                const showStdPrice = hasOffer ? edOfferPrice.stdPrice : priceDisplay.strikethroughPrice;
                const showRnwlPrice = hasOffer ? edOfferPrice.rnwlPrice : priceDisplay.strikethroughPrice;
                const showOfferPrice = hasOffer ? edOfferPrice.displayPrice : priceDisplay.mainPrice;
                const discPct = hasOffer
                  ? Math.round((1 - Number(edOfferPrice.displayPrice.replace(/,/g, '')) / Number(edOfferPrice.stdPrice.replace(/,/g, ''))) * 100)
                  : priceDisplay.discountPercent;
                const handleMobileCardBuyNow = () => {
                  if (hasOffer) { handleUseCoupon(activeOffer.couponCode, edition.id, activeOffer.planPeriod); }
                  else { handleBuyNowWithDefaultCoupon(edition.id); }
                  onPackageClick?.(edition.id);
                };
                return (
                  <div key={edition.id} className="bg-background-light border border-border rounded p-6 text-center flex flex-col shadow-[0_2px_5px_rgba(0,0,0,0.05)]">
                    <style>{`@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>
                    <h2 className="text-[24px] font-bold text-text-primary mt-0 mb-1">{edition.name}</h2>
                    <p className="text-[14px] text-text-primary mb-4 italic">{edition.tagline || 'Complete business solution for your enterprise'}</p>

                    {pageName?.toUpperCase() === 'GERP' ? (
                      <>
                        <p className="text-sm text-text-secondary mb-2">Regular Rate: <span className="text-[11px]">₹</span> <span className="text-lg font-bold">{showStdPrice}</span> <span className="text-xs">/ year +GST</span></p>
                        <p className="text-lg font-bold text-text-secondary mb-3">Start 1st year @ just</p>
                      </>
                    ) : (
                      <p className="text-lg font-bold text-text-secondary mb-3">All Features & Integrations <span className='text-wrap'>mentioned above @ just</span></p>
                    )}

                    <div className="flex flex-col items-center gap-1 mb-3">
                      {showStdPrice && (
                        <div className="flex items-baseline gap-1 text-text-secondary opacity-60">
                          <span className="text-2xl mr-1 font-semibold">₹</span>
                          <span className="text-4xl line-through font-bold">{hasOffer ? showRnwlPrice : showStdPrice}</span>
                        </div>
                      )}
                      <div className="flex items-end gap-1 mt-3 leading-none">
                        <span className="text-[32px] mr-1 text-primary font-semibold mb-1 opacity-60">₹</span>
                        <span className="text-[60px] font-extrabold text-primary leading-none tracking-tight">{showOfferPrice}</span>
                      </div>
                      {pageName?.toUpperCase() != 'GERP' &&
                        <p className="text-[18px] text-text-secondary mt-1">{(hasOffer || edition.id === 37) ? 'for 1st year +GST' : 'per year +GST'}</p>
                      }
                    </div>

                    {flashOfferOn && (
                      <div className="text-primary text-xl font-bold px-5 py-1 mb-2" style={{ animation: 'blink 2s ease-out infinite' }}>
                        Flat <span className="text-lg opacity-60 text-black">₹</span> {(Number((hasOffer ? showRnwlPrice : showStdPrice).replace(/,/g, '')) - Number(showOfferPrice.replace(/,/g, ''))).toLocaleString('en-IN')} OFF!
                      </div>
                    )}
                    {discPct > 0 && !flashOfferOn && (
                      <div className="text-primary text-xl font-bold px-5 py-1 mb-2" style={{ animation: 'blink 2s ease-in-out infinite' }}>
                        {discPct}% OFF!
                      </div>
                    )}

                    <div className="flex flex-col items-center gap-2 text-[14px] my-4 text-text-primary">
                      {edition.pricingLines && edition.pricingLines.length > 0 ? (
                        edition.pricingLines.map((line, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <span className="text-primary font-bold">+</span>
                            <span><strong>{line}</strong></span>
                          </div>
                        ))
                      ) : (
                        <>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-primary font-bold">+</span>
                            <span><strong>Free Support</strong></span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-primary font-bold">+</span>
                            <span><strong>Unlimited User Access</strong> (Desktop + Mobile)</span>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="flex flex-col gap-3 mt-2">
                      <Button onClick={handleMobileCardBuyNow} className="w-full">
                        Buy Now
                      </Button>
                      {getCouponsByEdition(edition.id).length > 0 && !hasOffer && !isFromWebsite && (
                        <Button
                          onClick={() => { handleViewCoupons(edition); onCouponView?.(edition.name); }}
                          variant="secondary"
                          className="w-full bg-primary-subtle hover:bg-primary-subtle"
                        >
                          Check Offers
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        {/* {visibleEditions.length > 1 && (
          <p className="text-[14px] text-text-secondary mt-6">(GST extra. Limited time offer!)</p>
        )} */}
        {/* Footer Note */}
        <ConditionalDiv className="mt-8 text-text-primary">
          {visibleEditions.some(e => e.rfdAvl) && (
            <p className="font-bold text-base">
              Each package is offered with 7-day Moneyback Guarantee
            </p>
          )}
        </ConditionalDiv>
      </Container>

      {/* Coupon Modal */}
      <CouponModal />
      <IOSRestrictedModal isOpen={showIOSModal} onClose={() => setShowIOSModal(false)} />
    </section>
  );
};

export default PackagesSection;